export const VEHICLE_SELECTION_GENERIC_ERROR =
  "We could not load this vehicle configuration. Please try again or choose another vehicle."

export const PDM_PRODUCTS_UNAVAILABLE_ERROR =
  "We could not load product data for this vehicle. Please try again."

export type VehicleSelectionContext = {
  make?: string
  model?: string
  year?: string
}

export type VehicleSelectionErrorDisplay = {
  title: string
  message: string
}

function isDevelopment(): boolean {
  return process.env.NODE_ENV === "development"
}

function formatYmm(context?: VehicleSelectionContext): string {
  const { year, make, model } = context ?? {}
  if (year && make && model) {
    return `${year} ${make} ${model}`
  }
  if (make && model) {
    return `${make} ${model}`
  }
  return ""
}

/** Dato GraphQL returned no `upholsteryCar` for the selected YMM. */
export class VehicleNotFoundError extends Error {
  readonly make: string
  readonly model: string
  readonly year: string

  constructor(make: string, model: string, year: string) {
    super(`Vehicle configuration not found for ${year} ${make} ${model}`)
    this.name = "VehicleNotFoundError"
    this.make = make
    this.model = model
    this.year = year
  }
}

/** Dato record exists but has no configuration steps. */
export class DatoStepsMissingError extends Error {
  readonly make: string
  readonly model: string
  readonly year: string

  constructor(make: string, model: string, year: string) {
    super(
      `Dato vehicle has no configuration steps for ${year} ${make} ${model}`
    )
    this.name = "DatoStepsMissingError"
    this.make = make
    this.model = model
    this.year = year
  }
}

function displayForVehicleNotFoundInternal(
  error: VehicleNotFoundError
): VehicleSelectionErrorDisplay {
  const ymm = `${error.year} ${error.make} ${error.model}`
  return {
    title: "Vehicle not found in Dato CMS",
    message: `No upholstery record exists in Dato for ${ymm}. This vehicle may appear in the PDM vehicle list but still needs an UpholsteryCar entry in Dato before it can be configured.`,
  }
}

function displayForVehicleNotFoundUser(
  error: VehicleNotFoundError
): VehicleSelectionErrorDisplay {
  const ymm = `${error.year} ${error.make} ${error.model}`
  return {
    title: "Vehicle not available",
    message: `We couldn't find a configuration for ${ymm}. Please try another vehicle or contact us at 888-460-0640 for help.`,
  }
}

function displayForDatoStepsMissingInternal(
  error: DatoStepsMissingError
): VehicleSelectionErrorDisplay {
  const ymm = `${error.year} ${error.make} ${error.model}`
  return {
    title: "Vehicle configuration is incomplete in Dato",
    message: `Dato has a record for ${ymm}, but it has no configuration steps. Add or publish the vehicle's step structure in Dato CMS.`,
  }
}

function displayForDatoStepsMissingUser(
  error: DatoStepsMissingError
): VehicleSelectionErrorDisplay {
  const ymm = `${error.year} ${error.make} ${error.model}`
  return {
    title: "Configuration unavailable",
    message: `We couldn't load a complete configuration for ${ymm}. Please try another vehicle or contact us at 888-460-0640 for help.`,
  }
}

function displayForPdmStrictErrorInternal(
  message: string,
  context?: VehicleSelectionContext
): VehicleSelectionErrorDisplay | null {
  const ymm = formatYmm(context) || "this vehicle"

  if (message.includes("No PDM products available")) {
    return {
      title: "PDM product data is empty",
      message:
        "The PDM API returned no products. Check the PDM API token, network connection, and that product data is available for this environment.",
    }
  }

  if (message.includes("No matching applications found for")) {
    return {
      title: "No PDM products match this vehicle",
      message: `PDM products are loaded, but none match ${ymm}. The vehicle may need PDM application data (make, model, and year) before it can be configured.`,
    }
  }

  if (message.includes("Failed to extract required options")) {
    const missing = message
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.startsWith("- "))
      .map((line) => line.slice(2))
      .join("; ")

    return {
      title: "PDM data is incomplete for this vehicle",
      message: missing
        ? `PDM matched ${ymm}, but required configuration data is missing: ${missing}.`
        : `PDM matched ${ymm}, but required body, rows, or factory match options could not be built from the product data.`,
    }
  }

  return null
}

function displayForPdmStrictErrorUser(
  _message: string,
  context?: VehicleSelectionContext
): VehicleSelectionErrorDisplay | null {
  const ymm = formatYmm(context)
  return {
    title: "Unable to load vehicle configuration",
    message: ymm
      ? `We couldn't load the configuration for ${ymm}. Please try again or choose another vehicle.`
      : VEHICLE_SELECTION_GENERIC_ERROR,
  }
}

function toInternalVehicleSelectionErrorDisplay(
  error: unknown,
  context?: VehicleSelectionContext
): VehicleSelectionErrorDisplay {
  if (error instanceof VehicleNotFoundError) {
    return displayForVehicleNotFoundInternal(error)
  }

  if (error instanceof DatoStepsMissingError) {
    return displayForDatoStepsMissingInternal(error)
  }

  if (error instanceof Error) {
    if (error.message === PDM_PRODUCTS_UNAVAILABLE_ERROR) {
      const ymm = formatYmm(context)
      return {
        title: "PDM product data unavailable",
        message: ymm
          ? `We could not load PDM product data for ${ymm}. Check your network connection and PDM API settings, then try again.`
          : PDM_PRODUCTS_UNAVAILABLE_ERROR,
      }
    }

    const pdmDisplay = displayForPdmStrictErrorInternal(error.message, context)
    if (pdmDisplay) {
      return pdmDisplay
    }

    if (error.message && error.message !== VEHICLE_SELECTION_GENERIC_ERROR) {
      return {
        title: "Unable to load vehicle configuration",
        message: error.message,
      }
    }
  }

  const ymm = formatYmm(context)
  return {
    title: "Unable to load vehicle configuration",
    message: ymm
      ? `We could not load the configuration for ${ymm}. Please try again or choose another vehicle.`
      : VEHICLE_SELECTION_GENERIC_ERROR,
  }
}

/** User-facing copy — no internal system names (Dato, PDM, etc.). */
export function toVehicleSelectionErrorDisplay(
  error: unknown,
  context?: VehicleSelectionContext
): VehicleSelectionErrorDisplay {
  if (error instanceof VehicleNotFoundError) {
    return displayForVehicleNotFoundUser(error)
  }

  if (error instanceof DatoStepsMissingError) {
    return displayForDatoStepsMissingUser(error)
  }

  if (error instanceof Error) {
    if (error.message === PDM_PRODUCTS_UNAVAILABLE_ERROR) {
      const ymm = formatYmm(context)
      return {
        title: "Unable to load vehicle configuration",
        message: ymm
          ? `We couldn't load the configuration for ${ymm}. Please try again or choose another vehicle.`
          : VEHICLE_SELECTION_GENERIC_ERROR,
      }
    }

    if (displayForPdmStrictErrorInternal(error.message, context)) {
      return displayForPdmStrictErrorUser(error.message, context)!
    }

    if (error.message && error.message !== VEHICLE_SELECTION_GENERIC_ERROR) {
      return {
        title: "Unable to load vehicle configuration",
        message: VEHICLE_SELECTION_GENERIC_ERROR,
      }
    }
  }

  const ymm = formatYmm(context)
  return {
    title: "Unable to load vehicle configuration",
    message: ymm
      ? `We couldn't load the configuration for ${ymm}. Please try again or choose another vehicle.`
      : VEHICLE_SELECTION_GENERIC_ERROR,
  }
}

/** @deprecated Use `toVehicleSelectionErrorDisplay` — returns detail text only. */
export function toVehicleSelectionErrorMessage(
  error: unknown,
  context?: VehicleSelectionContext
): string {
  return toVehicleSelectionErrorDisplay(error, context).message
}

type VehicleSelectionErrorLogDetails = {
  vehicle: VehicleSelectionContext
  errorType: string
  cause: string
  detail: string
  failedStep: string
  hint: string
  rawMessage?: string
}

function buildVehicleSelectionErrorLogDetails(
  error: unknown,
  context: VehicleSelectionContext
): VehicleSelectionErrorLogDetails {
  const display = toInternalVehicleSelectionErrorDisplay(error, context)
  const ymm = formatYmm(context) || "(unknown YMM)"

  const base: VehicleSelectionErrorLogDetails = {
    vehicle: context,
    errorType: error instanceof Error ? error.name : typeof error,
    cause: display.title,
    detail: display.message,
    failedStep: "unknown",
    hint: "See detail above and the thrown error below.",
    rawMessage: error instanceof Error ? error.message : String(error),
  }

  if (error instanceof VehicleNotFoundError) {
    return {
      ...base,
      failedStep:
        "loadUpholsteryVehicleSelection → Dato upholsteryStepsQuery returned null",
      hint: `Create or publish an UpholsteryCar in Dato for ${ymm}. PDM picker can list vehicles that Dato does not have yet.`,
    }
  }

  if (error instanceof DatoStepsMissingError) {
    return {
      ...base,
      failedStep: "resolveStepsWithPdm → Dato vehicle record has no steps[]",
      hint: `Open the UpholsteryCar in Dato for ${ymm} and add/publish the step structure.`,
    }
  }

  if (error instanceof Error) {
    if (error.message === PDM_PRODUCTS_UNAVAILABLE_ERROR) {
      return {
        ...base,
        failedStep:
          "resolveStepsWithPdm → fetchPdmProductsRecords returned empty",
        hint: "Check NEXT_PUBLIC_PDM_AUTOMOTIVE_API_TOKEN, PDM API connectivity, and that products exist for this environment.",
      }
    }

    if (error.message.includes("No PDM products available")) {
      return {
        ...base,
        failedStep:
          "mergeConfigurableStepsFromPdmProducts → PDM products array is empty",
        hint: "PDM V2 is enabled but no products were returned. Verify PDM API token and brand/product data.",
      }
    }

    if (error.message.includes("No matching applications found for")) {
      return {
        ...base,
        failedStep:
          "mergeConfigurableStepsFromPdmProducts → flattenMatchingApplications returned 0 rows",
        hint: `PDM has products, but none match ${ymm}. Check PDM application make/model/year on the product records.`,
      }
    }

    if (error.message.includes("Failed to extract required options")) {
      return {
        ...base,
        failedStep:
          "mergeConfigurableStepsFromPdmProducts → validation failed after MMY match",
        hint: `PDM matched ${ymm} but body/rows/factory-match options could not be built. Inspect part_attributes on matched products.`,
      }
    }
  }

  return {
    ...base,
    failedStep: "loadUpholsteryVehicleSelection or resolveStepsWithPdm",
    hint: `Could not load configuration for ${ymm}.`,
  }
}

/** Structured console output when vehicle selection fails (development only). */
export function logVehicleSelectionError(
  error: unknown,
  context: VehicleSelectionContext
): void {
  if (!isDevelopment()) {
    return
  }

  const details = buildVehicleSelectionErrorLogDetails(error, context)
  const ymm = formatYmm(context) || "(unknown vehicle)"

  // ─────────────────────────────────────────────────────────────
  // DEBUG (for Project Managers): styled, plain-English summary of
  // why this vehicle's dropdown/modal shows an error. Safe to remove later.
  // ─────────────────────────────────────────────────────────────
  const bannerStyle =
    "background:#b71c1c;color:#fff;font-size:14px;font-weight:bold;padding:6px 10px;border-radius:4px;"
  const headingStyle =
    "color:#b71c1c;font-size:13px;font-weight:bold;padding:2px 0;"
  const labelStyle = "color:#555;font-weight:bold;"

  console.groupCollapsed(
    `%c⚠️ VEHICLE CANNOT BE CONFIGURED — ${ymm}`,
    bannerStyle
  )
  console.log(
    "%cWhat happened:%c " + details.cause,
    headingStyle,
    "color:#333;"
  )
  console.log("%cDetail:%c " + details.detail, headingStyle, "color:#333;")
  console.log("%cVehicle:%c " + ymm, labelStyle, "color:#333;")
  console.log("%cFailed at:%c " + details.failedStep, labelStyle, "color:#333;")
  console.log("%cWhat to do:%c " + details.hint, headingStyle, "color:#333;")
  if (details.rawMessage) {
    console.log(
      "%cRaw message:%c " + details.rawMessage,
      labelStyle,
      "color:#333;"
    )
  }
  console.groupEnd()

  console.group("[upholstery] Vehicle selection failed")
  console.error("Vehicle:", details.vehicle)
  console.error("Error type:", details.errorType)
  console.error("Cause:", details.cause)
  console.error("Detail:", details.detail)
  console.error("Failed at:", details.failedStep)
  console.error("Hint:", details.hint)
  if (details.rawMessage) {
    console.error("Raw message:", details.rawMessage)
  }
  console.error("Thrown error:", error)
  console.groupEnd()
}
